import { Brain, Search, Bell, Settings, User, LogOut, Building2, CreditCard, Package, Hotel } from "lucide-react"
import React from "react"
import { useNavigate } from "react-router-dom"
import { GlobalSearch } from "./global-search"
import { MobileNavigation } from "./mobile-navigation"
import { LanguageSwitcher } from "./language-switcher"
import { TranslatedText } from "./translated-text"
import { useAuth } from "../contexts/auth-context"
import { useSidebar } from "../contexts/sidebar-context"
import { Button } from "./ui/button"
import { NotificationDropdown } from "./notification-dropdown"
import { MenuQrCode } from "./menu-qr-code"
import { QrCode } from "lucide-react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { apiService } from "../lib/api"
import { Alert, AlertDescription, AlertTitle } from "./ui/alert"
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu"

export function Header() {
  const { user, logout, isAuthenticated, isLoading } = useAuth()
  const { isCollapsed } = useSidebar()
  const navigate = useNavigate()
  const [mounted, setMounted] = React.useState(false)
  React.useEffect(() => setMounted(true), [])
  const qc = useQueryClient()
  
  React.useEffect(() => {
    // Invalidate companies cache when user or role changes
    if (user?.role) {
      qc.invalidateQueries({ queryKey: ['companies'] });
    }
  }, [user?.role, qc])
  
  const { data: companies } = useQuery({
    queryKey: ['companies', user?.role], // Include user role in cache key to refresh when role changes
    queryFn: async () => {
      const userRole = user?.role as string;
      const isSuperAdmin = userRole === 'super_admin';
      
      try {
        // For super_admin, use getCompanies which returns all companies
        // For others, use getMyCompanies which returns only assigned companies
        if (isSuperAdmin) {
          const result = await apiService.getCompanies({ page: 1, pageSize: 100 }) as any;
          // Handle different response formats
          if (Array.isArray(result) && result.length > 0) return result;
          if (result?.data && Array.isArray(result.data) && result.data.length > 0) return result.data;
          if (result?.items && Array.isArray(result.items) && result.items.length > 0) return result.items;
          return [];
        } else {
          // For non-super-admin users, only fetch their assigned companies
          const myCompanies = await apiService.getMyCompanies();
          return myCompanies.map((uc: any) => uc.company || uc);
        }
      } catch (error) {
        console.warn('Header: Failed to fetch companies, trying fallback', error);
        try {
          // Fallback: try getMyCompanies for all users
          const myCompanies = await apiService.getMyCompanies();
          return myCompanies.map((uc: any) => uc.company || uc);
        } catch (e) {
          console.error('Header: Both company fetch methods failed', e);
          return [];
        }
      }
    },
    enabled: isAuthenticated && !!user
  })
  
  const companiesList = React.useMemo(() => {
    if (!companies) return [];
    
    let allCompanies = [];
    if (Array.isArray(companies)) {
      allCompanies = companies;
    } else if (companies.data && Array.isArray(companies.data)) {
      allCompanies = companies.data;
    } else if (companies.items && Array.isArray(companies.items)) {
      allCompanies = companies.items;
    } else {
      return [];
    }
    
    // Process companies to extract status from companySettings (same as companies page)
    const processedCompanies = allCompanies.map((company: any) => {
      if (company.companySettings && Array.isArray(company.companySettings)) {
        const settings = company.companySettings;
        const status = settings.find((s: any) => s.key === 'status')?.value || 'active';
        return { ...company, status };
      }
      return { ...company, status: company.status || 'active' };
    });
    
    // Filter out inactive companies - header should only show active companies
    return processedCompanies.filter((company: any) => {
      const status = company.status || 'active';
      return status !== 'inactive' && status !== 'INACTIVE';
    });
  }, [companies])
  
  // Get active company from localStorage
  const [activeCompany, setActiveCompany] = React.useState<string | undefined>(() => {
    if (typeof window === 'undefined') return undefined;
    return localStorage.getItem('company_id') || undefined;
  })
  
  const [activeCompanyName, setActiveCompanyName] = React.useState<string | undefined>(() => {
    if (typeof window === 'undefined') return undefined;
    return localStorage.getItem('company_name') || undefined;
  })
  
  // Listen for company changes in localStorage
  React.useEffect(() => {
    if (!mounted) return
    
    const updateCompany = () => {
      try {
        const companyId = localStorage.getItem('company_id') || localStorage.getItem('companyId') || localStorage.getItem('company')
        const companyName = localStorage.getItem('company_name')
        
        if (companyId && companyId !== activeCompany) {
          setActiveCompany(companyId)
        }
        if (companyName && companyName !== activeCompanyName) {
          setActiveCompanyName(companyName)
        }
      } catch (error) {
        console.error('❌ Error reading company from localStorage:', error)
      }
    }
    
    // Initial update
    updateCompany()
    
    // Listen for storage changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'company_id' || e.key === 'companyId' || e.key === 'company' || e.key === 'company_name') {
        updateCompany()
      }
    }
    
    // Listen for custom company change event
    const handleCompanyChange = (e: CustomEvent) => {
      updateCompany()
    }
    
    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('companyChanged', handleCompanyChange as EventListener)
    
    // Periodic check to sync with localStorage
    const interval = setInterval(updateCompany, 1000)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('companyChanged', handleCompanyChange as EventListener)
      clearInterval(interval)
    }
  }, [mounted, activeCompany, activeCompanyName])

  const onCompanyChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCompanyId = e.target.value
    const selectedCompany = companiesList.find((c: any) => c.id === newCompanyId)
    
    if (selectedCompany) {
      // Validate access: super_admin can access all, admin can only access assigned companies
      const userRole = user?.role as string
      const isSuperAdmin = userRole === 'super_admin'
      
      // If not super admin, verify the company is in the list (which is already filtered by backend)
      if (!isSuperAdmin && !companiesList.find((c: any) => c.id === newCompanyId)) {
        console.error('❌ Access denied: You do not have access to this company')
        // Reset to previous company
        const previousCompanyId = localStorage.getItem('company_id')
        if (previousCompanyId && e.target) {
          e.target.value = previousCompanyId
        }
        return
      }
      
      try {
        localStorage.setItem('company_id', newCompanyId)
        localStorage.setItem('companyId', newCompanyId)
        localStorage.setItem('company', newCompanyId)
        if (selectedCompany.name) {
          localStorage.setItem('company_name', selectedCompany.name)
        }
        
        // Dispatch custom event for other components
        window.dispatchEvent(new CustomEvent('companyChanged', { 
          detail: { companyId: newCompanyId, companyName: selectedCompany.name } 
        }))
        
        // Refresh the page to ensure all components reload with the new company
        window.location.reload()
      } catch (error) {
        console.error('❌ Error saving company to localStorage:', error)
      }
    }
  }

  if (!mounted) {
    return (
      <header 
        className={`fixed top-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border transition-all duration-300 ${
          isCollapsed ? 'left-0 lg:left-16' : 'left-0 lg:left-64'
        }`} 
        suppressHydrationWarning
      >
        <div className="h-14 lg:h-16" />
      </header>
    )
  }

  // Don't render if auth is still loading
  if (isLoading) {
    return (
      <header className="h-16 bg-white border-b border-gray-200 px-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="h-8 w-8 bg-gray-200 rounded-full animate-pulse"></div>
        </div>
      </header>
    )
  }

  return (
    <header 
      className={`fixed top-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border transition-all duration-300 ${
        isCollapsed ? 'left-0 lg:left-16' : 'left-0 lg:left-64'
      }`}
    >
      <div className="flex items-center justify-between pl-0 pr-2 py-3 lg:pl-0 lg:pr-4 lg:py-4">
        {/* Left side - Logo and Brand */}
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center">
            <img 
              src="/brand-b.png" 
              alt="Ikoro House" 
              className="w-8 h-8 object-contain"
            />
          </div>
          <div className="hidden sm:block">
            <h1 className="text-base font-bold text-foreground">Ikoro House</h1>
            <p className="text-xs text-muted-foreground">Hotel Management</p>
          </div>
        </div>

        {/* Center - Search and Client Selector */}
        <div className="hidden md:flex items-center gap-4 flex-1 max-w-2xl mx-8">
          <div className="flex-1">
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <select 
              value={activeCompany || ''} 
              onChange={onCompanyChange} 
              className="px-3 py-2 bg-card border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary min-w-[200px] cursor-pointer"
              disabled={!isAuthenticated || companiesList.length === 0}
              title={activeCompanyName || "Select a company"}
            >
              {companiesList.length > 0 ? (
                companiesList.map((c: any) => (
                  <option key={c.id} value={c.id}>{c.name || c.id}</option>
                ))
              ) : (
                <option value=""><TranslatedText text="No companies available" /></option>
              )}
            </select>
          </div>
        </div>

        {/* Right side - Actions and User */}
        <div className="flex items-center gap-2">
          {/* Language Switcher */}
          <div className="hidden md:block">
            <LanguageSwitcher />
          </div>
          
          <button className="p-2 hover:bg-muted rounded-lg transition-colors">
            <Search className="w-4 h-4 md:hidden" />
          </button>
          
          <NotificationDropdown />
          {/* Settings Button - Only for Admin and Super Admin */}
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <button 
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              onClick={() => navigate('/dashboard/settings')}
              title="Settings"
            >
              <Settings className="w-4 h-4" />
            </button>
          )}
          
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src="" alt={user.firstName} />
                    <AvatarFallback>
                      {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user.email}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user.companyName}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/profile')} className="focus:bg-primary focus:text-white group">
                  <User className="mr-2 h-4 w-4 group-focus:text-white" />
                  <span><TranslatedText text="Profile" /></span>
                </DropdownMenuItem>
                {/* Settings - Only for Admin and Super Admin */}
                {(user?.role === 'admin' || user?.role === 'super_admin') && (
                  <DropdownMenuItem onClick={() => navigate('/dashboard/settings')} className="focus:bg-primary focus:text-white group">
                    <Settings className="mr-2 h-4 w-4 group-focus:text-white" />
                    <span><TranslatedText text="Settings" /></span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => logout()} className="focus:bg-primary focus:text-white group">
                  <LogOut className="mr-2 h-4 w-4 group-focus:text-white" />
                  <span><TranslatedText text="Log out" /></span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" size="sm">
              <User className="w-4 h-4" />
            </Button>
          )}
          
          <div className="md:hidden">
            <MobileNavigation />
          </div>
        </div>
      </div>

      {/* Company prompt when none exists */}
      {/* {mounted && companiesList.length === 0 && (
        <div className="px-4 lg:px-6 pb-3">
          <Alert>
            <AlertTitle>No company found</AlertTitle>
            <AlertDescription>
              Create a company to start recording transactions. Go to Settings to add your first company.
            </AlertDescription>
          </Alert>
        </div>
      )} */}
    </header>
  )
}
